import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import io from "socket.io-client";
import api, { IMAGE_BASE_URL, GOOGLE_MAPS_KEY } from "../api/Api.js";
import {
  GoogleMap,
  Marker,
  Polyline,
  useJsApiLoader,
} from "@react-google-maps/api";
import { MapPin, Car, FileText } from "lucide-react";
import Swal from "sweetalert2";
import { toast } from "sonner";

const SOCKET_URL = IMAGE_BASE_URL;

// ─── Distance helper (meters) ────────────────────────────────
const getDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3;
  const p1 = (lat1 * Math.PI) / 180;
  const p2 = (lat2 * Math.PI) / 180;
  const dp = ((lat2 - lat1) * Math.PI) / 180;
  const dl = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const SendLocationPage = () => {
  const [showOtpPopup, setShowOtpPopup] = useState(false);
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [tsId, setTsId] = useState(null);
  const [tripEnded, setTripEnded] = useState(false);
  const [nextTrip, setNextTrip] = useState(null);

  const [odometerImage, setOdometerImage] = useState(null);
  const [odometerReading, setOdometerReading] = useState("");
  const [odometerPreview, setOdometerPreview] = useState(null);

  const navigate = useNavigate();
  const navState = useLocation().state || {};
  const START_LAT = navState.start_lat ? Number(navState.start_lat) : null;
  const START_LNG = navState.start_lng ? Number(navState.start_lng) : null;

  const hideEndTrip = navState.hideEndTrip || false;
  const routedToLiveCustomer = useRef(false);
  const lastRoutedCustomerPos = useRef(null);

  const [driverId, setDriverId] = useState(null);
  const [socket, setSocket] = useState(null);
  const [status, setStatus] = useState("Connecting...");
  const [todayTrips, setTodayTrips] = useState([]);
  const [loadingTrips, setLoadingTrips] = useState(true);

  // ── Map / route states ────────────────────────────────────
  const [heading, setHeading] = useState(0);
  const [startPoint, setStartPoint] = useState(null);
  const [routeToPickup, setRouteToPickup] = useState([]); // blue
  const [drivenPath, setDrivenPath] = useState([]); // green
  const [currentPosition, setCurrentPosition] = useState(null);
  const [routeError, setRouteError] = useState("");

  // ########### bijay############
  const [customerPosition, setCustomerPosition] = useState(null);
  const lastSentTime = useRef(0);
  const directionsService = useRef(null);
  const mapRef = useRef(null);
  const routeBuilt = useRef(false);
  const containerStyle = { width: "100%", height: "400px" };

  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_KEY });

  // ============================================================
  // 1. INIT
  // ============================================================
  useEffect(() => {
    const stored = localStorage.getItem("driverRegNo");
    setDriverId(stored);
    fetchAssignedTrip();
  }, []);

  const fetchAssignedTrip = async () => {
    try {
      setLoadingTrips(true);
      const regNo = localStorage.getItem("driverRegNo");
      if (!regNo) return setTodayTrips([]);
      const res = await api.get(`/driver/assigned-trip?driver_regno=${regNo}`);
      const tripData = Array.isArray(res.data) ? res.data : [res.data];
      setTodayTrips(tripData.length ? tripData : []);
    } catch (err) {
      console.error(err);
      setTodayTrips([]);
    } finally {
      setLoadingTrips(false);
    }
  };

  // ============================================================
  // 2. SOCKET
  // ============================================================
  useEffect(() => {
    const newSocket = io(SOCKET_URL, { transports: ["websocket"] });
    newSocket.on("connect", () => setStatus("Connected ✅"));
    newSocket.on("connect_error", () => setStatus("Connection failed ❌"));
    setSocket(newSocket);
    return () => newSocket.disconnect();
  }, []);

  // ── Geocode: address name → { lat, lng } ───────────────────
  const geocode = (address) =>
    new Promise((resolve, reject) => {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { address, componentRestrictions: { country: "IN" } },
        (results, status) => {
          if (status === "OK" && results[0]) {
            const loc = results[0].geometry.location;
            resolve({ lat: loc.lat(), lng: loc.lng() });
          } else {
            reject(`Geocode failed for "${address}": ${status}`);
          }
        },
      );
    });

  // ── Build blue planned route ────────────────────────────────
  // 🟢 2. UPGRADE THIS FUNCTION
  const buildRouteToPickup = async (driverPos, liveCustomerPos = null) => {
    try {
      setStatus("Calculating route...");

      let destinationPos;

      // A. If we have the customer's LIVE GPS, use that exact spot!
      if (liveCustomerPos && liveCustomerPos.lat && liveCustomerPos.lng) {
        destinationPos = liveCustomerPos;
      }
      // B. Otherwise, fallback to guessing based on the text address
      else {
        const pickupName = todayTrips?.[0]?.pickup_location;
        if (!pickupName) {
          setRouteError("Pickup location missing.");
          return;
        }
        destinationPos = await geocode(pickupName);
      }

      if (!directionsService.current)
        directionsService.current = new window.google.maps.DirectionsService();

      directionsService.current.route(
        {
          origin: driverPos,
          destination: destinationPos,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, routeStatus) => {
          if (routeStatus === "OK") {
            const points = result.routes[0].overview_path.map((p) => ({
              lat: p.lat(),
              lng: p.lng(),
            }));
            setRouteToPickup(points);
            setStatus("Live tracking active 🟢");
          } else {
            setRouteError(`Route error: ${routeStatus}`);
          }
        },
      );
    } catch (err) {
      setRouteError(String(err));
    }
  };

  // ============================================================
  // 5. GPS LIVE TRACKING — auto-starts, no button needed
  // ============================================================
  useEffect(() => {
    if (!driverId || !socket) return;

    socket.emit("join_app_session", driverId);

    if (!navigator.geolocation) {
      setStatus("Geolocation not supported");
      return;
    }

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const lat = Number(pos.coords.latitude);
        const lng = Number(pos.coords.longitude);
        const accuracy = pos.coords.accuracy || 0;
        const spd = pos.coords.speed ? (pos.coords.speed * 3.6).toFixed(1) : 0; 

      //  if (accuracy > 50) return; // commentet  in local  for  testing  by  bijay 
       // Filter out bad GPS jumps
        setHeading(Number(pos.coords.heading) || 0);
        setCurrentPosition({ lat, lng });
        // setSpeed(spd);

        // Grow green driven trail
        setDrivenPath((prev) => {
          if (!prev.length) return [{ lat, lng }];
          const last = prev[prev.length - 1];
          const dist = getDistance(last.lat, last.lng, lat, lng);
          if (dist > 5) {
            return [...prev, { lat, lng }];
          }
          return prev;
        });

        // Follow driver on map
        if (mapRef.current) mapRef.current.panTo({ lat, lng });

        const activeTrip = todayTrips?.[0];
        const roomID = activeTrip?.enqref_no || activeTrip?.enq_id;
        
        // Check if the trip has actually started
        const isTripStarted = activeTrip?.otp_verified === 1;

        //  ONLY send the driver's location IF the trip hasn't started yet!
        if (roomID && !isTripStarted) {
          // console.log(`🚗 [DRIVER APP] Sending live GPS to room: ${roomID}`);
          socket.emit("driver_live_location", {
            bookingId: roomID,
            lat: lat,
            lng: lng,
            heading: Number(pos.coords.heading) || 0
          });
        } else if (isTripStarted) {
          // This proves it successfully stopped broadcasting!
          // console.log(" [DRIVER APP] Trip started. Muted driver GPS broadcast to customer.");
        }

        // =========================================================
        //  2. SLOW UPDATE: Emit to Backend Database (Throttled)
        // =========================================================
        const now = Date.now();
        if (now - lastSentTime.current < 15000) return; // Stop here if 15s hasn't passed!
        lastSentTime.current = now;

        const enqueryId = todayTrips?.[0]?.enq_id;
        const payload = {
          enqueryId,
          driverId,
          lat,
          lng,
          speed: Number(pos.coords.speed) || 0,
          heading: Number(pos.coords.heading) || 0,
          timestamp: new Date().toISOString(),
        };

        setStatus(`Live • ${new Date().toLocaleTimeString()}`);
        socket.emit("driver_location_insert", payload); 
      },
      () => setStatus("GPS Permission Denied ❌"),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 },
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [driverId, socket, todayTrips]);

  //  LISTEN FOR CUSTOMER LIVE LOCATION  BIJAY
  useEffect(() => {
    if (!socket || !todayTrips || !todayTrips[0]) return;

    const roomID = todayTrips[0].enqref_no || todayTrips[0].enq_id;

    if (roomID) {
      // 1. Join the private meet-up room for this specific trip
      socket.emit("join_booking_room", roomID);
    }

    // 2. Listen for customer location updates
    socket.on("update_customer_location", (data) => {
      // console.log("Received Customer Location:", data);
      setCustomerPosition({ lat: data.lat, lng: data.lng });
    });

    // 3. Cleanup listener on unmount or trip change
    return () => {
      socket.off("update_customer_location");
    };
  }, [socket, todayTrips]);

  //  3. UPDATED: DYNAMICALLY DRAW ROUTE TO LIVE CUSTOMER
  useEffect(() => {
    const activeTrip = todayTrips?.[0];
    if (
      !isLoaded ||
      !currentPosition ||
      !customerPosition ||
      activeTrip?.otp_verified === 1
    ) {
      return;
    }

    // 1. If we have NEVER drawn the live route yet, do it now!
    if (!routedToLiveCustomer.current) {
      routedToLiveCustomer.current = true;
      lastRoutedCustomerPos.current = customerPosition; // Save their current spot
      // console.log("🗺️ Initial draw to Customer's Live GPS!");
      buildRouteToPickup(currentPosition, customerPosition);
    }
    // 2. If we ALREADY drew it, check if the customer has moved significantly (> 20 meters)
    else if (lastRoutedCustomerPos.current) {
      const distanceMoved = getDistance(
        lastRoutedCustomerPos.current.lat,
        lastRoutedCustomerPos.current.lng,
        customerPosition.lat,
        customerPosition.lng,
      );

      if (distanceMoved > 20) {
        // If they moved more than 20 meters
        // console.log(
        //   `🗺️ Customer moved ${Math.round(distanceMoved)}m. Redrawing route!`,
        // );
        lastRoutedCustomerPos.current = customerPosition; // Update saved spot
        buildRouteToPickup(currentPosition, customerPosition);
      }
    }
  }, [customerPosition, currentPosition, isLoaded, todayTrips]);
  // ============================================================

  const formatDistance = (m) =>
    m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(2)} km`;

  const trip = todayTrips?.[0];

  //  ADD THIS NEW FUNCTION: Builds the route to the Destination
  const buildRouteToDropoff = async (driverPos) => {
    try {
      const dropoffName = todayTrips?.[0]?.dropoff_location;
      if (!dropoffName) return;

      setStatus("Drawing route to drop-off...");

      const dropoffPos = await geocode(dropoffName);

      if (!directionsService.current)
        directionsService.current = new window.google.maps.DirectionsService();

      directionsService.current.route(
        {
          origin: driverPos,
          destination: dropoffPos,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, routeStatus) => {
          if (routeStatus === "OK") {
            const points = result.routes[0].overview_path.map((p) => ({
              lat: p.lat(),
              lng: p.lng(),
            }));
            setRouteToPickup(points);
            setStatus("Heading to Drop-off ");
          }
        },
      );
    } catch (err) {
      console.error("Dropoff Route Error:", err);
    }
  };

  //  UPDATE THIS USE-EFFECT
  useEffect(() => {
    if (!isLoaded || !START_LAT || !START_LNG || loadingTrips || !todayTrips[0])
      return;

    const trip = todayTrips[0];
    const currentDriverPos = { lat: START_LAT, lng: START_LNG };

    // Check if the trip has already started (OTP is verified)
    if (trip.otp_verified === 1) {
      // Stop listening for the customer's location
      setCustomerPosition(null);
      // Draw the route to the destination
      buildRouteToDropoff(currentDriverPos);
    } else {
      // Draw the route to the pickup point
      if (!routeBuilt.current) {
        routeBuilt.current = true;
        buildRouteToPickup(currentDriverPos);
      }
    }
  }, [isLoaded, loadingTrips, todayTrips]);

  useEffect(() => {
    if (!trip && !loadingTrips) {
      setTripEnded(true);
      fetchNextTrip();
    }
  }, [trip, loadingTrips]);

  const handleEndTrip = async () => {
    // 1. Show native-feeling SweetAlert Confirmation
    const result = await Swal.fire({
      title: "End Trip?",
      text: "Are you sure you want to end this trip?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545", // Red color for ending
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, End Trip",
      cancelButtonText: "Cancel",
      shape: "border-radius: 12px", // Softer edges
    });

    // If driver cancels, do nothing
    if (!result.isConfirmed) return;

    // 2. If confirmed, call API
    try {
      const enqId = trip?.enq_id;
      const res = await api.post("/driver/end-trip", {
        enq_id: enqId,
      });

      if (res.data.success) {
        setTsId(res.data.ts_id);
        setShowOtpPopup(true); // Triggers the slide-up OTP sheet
      }
    } catch (error) {
      Swal.fire("Error", "Failed to send OTP", "error");
    }
  };

  // Added on --19-03-2026-----------start---------------
  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          const MAX_WIDTH = 800;
          const scale = MAX_WIDTH / img.width;

          canvas.width = MAX_WIDTH;
          canvas.height = img.height * scale;

          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          canvas.toBlob(
            (blob) => {
              resolve(blob);
            },
            "image/jpeg",
            0.6,
          );
        };
      };

      reader.readAsDataURL(file);
    });
  };
  // Added on --19-03-2026-----------start---------------

  const handleOtpSubmit = async () => {
    const finalOtp = otp.join("");
    // ----------------------
    if (!odometerImage) {
      toast.warning("Please upload an odometer image before proceeding.", {
        description: "Odometer Image Required",
      });
      return;
    }

    if (!odometerReading) {
      toast.warning("Please enter the odometer reading before proceeding.", {
        description: "Odometer Reading Required",
      });
      return;
    }
    // ------------

    // Small validation: Ensure they typed all 4 digits before hitting submit
    if (finalOtp.length < 4) {
      Swal.fire({
        title: "Incomplete PIN",
        text: "Please enter the full 4-digit OTP.",
        icon: "warning",
        confirmButtonColor: "#f0ad4e",
        confirmButtonText: "Okay",
        background: "#ffffff",
        color: "#333333",
        customClass: { popup: "rounded-4 shadow-sm" },
      });
      return;
    }

    try {
      const compressedImage = await compressImage(odometerImage);
      const formData = new FormData();

      formData.append("ts_id", tsId);
      formData.append("otp", finalOtp);
      // formData.append("odo_img", odometerImage);

      formData.append("odo_img", compressedImage);
      // Added on --19-03-2026-----------start---------------
      formData.append("odo_read", odometerReading);
      formData.append("latitude", currentPosition?.lat || "");
      formData.append("longitude", currentPosition?.lng || "");

      const res = await api.post("/driver/verify-endtrip-otp", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data.success) {
        // 1. Native Success Alert
        await Swal.fire({
          title: "Trip Completed!",
          text: "The Trip has been successfully Ended.",
          icon: "success",
          confirmButtonColor: "#198754",
          confirmButtonText: "Continue to Dashboard",
          allowOutsideClick: false,
          customClass: { popup: "rounded-4 shadow-sm" },
        });

        // 2. Hide the OTP bottom sheet
        setShowOtpPopup(false);

        // 3. Update States
        setTripEnded(true);

        // 4. Fetch updated data
        fetchAssignedTrip();
        fetchNextTrip();

        // 5. Redirect back to dashboard safely
        navigate("/dashboard");
      }
    } catch (err) {
      // Native Error Alert
      const errorMessage =
        err.response?.data?.message || "Something went wrong";
      Swal.fire({
        title: errorMessage,
        text: "Something went wrong!! Please check and try again.",
        icon: "error",
        confirmButtonColor: "#dc3545", // Danger red
        confirmButtonText: "Retry",
        customClass: { popup: "rounded-4 shadow-sm" },
      });

      // Optional: Clear the OTP inputs automatically on failure so they can type again easily
      setOtp(["", "", "", ""]);
      if (otpRefs.current[0]) otpRefs.current[0].focus();
    }
  };

  const otpRefs = useRef([]);

  const handleOtpChange = (value, index) => {
    if (!/^[0-9]?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // move to next input automatically
    if (value && index < 3) {
      otpRefs.current[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

  const fetchNextTrip = async () => {
    try {
      const regNo = localStorage.getItem("driverRegNo");

      const res = await api.get(`/driver/next-trip?driver_regno=${regNo}`);

      if (res.data.length) {
        setNextTrip(res.data[0]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="bg-light min-vh-100 py-2">
      <div className="container" style={{ maxWidth: 520 }}>
        {/* ── Header Card ── */}
        <div
          className="d-flex justify-content-between align-items-center mb-3 p-3"
          style={{
            background: "linear-gradient(135deg,#0d6efd,#20c997)",
            borderRadius: "14px",
            boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
            color: "#fff",
          }}
        >
          {/* Left Section */}
          <div className="d-flex align-items-start gap-3">
            {/* Icon */}
            <div
              style={{
                background: "rgba(255,255,255,0.2)",
                borderRadius: "10px",
                padding: "8px",
              }}
            >
              <MapPin size={22} />
            </div>

            {/* Text */}
            <div>
              <h6 className="fw-bold mb-1">LIVE TRACKING MAP</h6>

              <div className="d-flex align-items-center gap-3 small">
                <span className="d-flex align-items-center gap-1">
                  <Car size={14} />
                  Driver: {driverId || "—"}
                </span>
              </div>
            </div>
          </div>

          {/* Live Badge */}
          <span
            className="badge d-flex align-items-center gap-2 px-3 py-2"
            style={{
              background: "rgba(255,255,255,0.25)",
              backdropFilter: "blur(6px)",
              borderRadius: "30px",
              fontSize: 12,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#00ff9d",
                animation: "pulse 1.4s infinite",
              }}
            />
            LIVE
          </span>
        </div>

        {/* ── Trip card ── */}

        <div
          className="card border-0 shadow-sm mb-3"
          style={{ borderRadius: 16, overflow: "hidden" }}
        >
          <div className="card-body p-3">
            {/* ── Trip Header ── */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center gap-2">
                <div
                  style={{
                    background: "#eef5ff",
                    padding: 8,
                    borderRadius: 8,
                  }}
                >
                  <Car size={18} color="#0d6efd" />
                </div>

                <div>
                  <div
                    className="text-muted"
                    style={{ fontSize: 11, letterSpacing: 1 }}
                  >
                    PICK UP TIME
                  </div>
                  <div className="fw-bold fs-5" style={{ color: "#1a1a2e" }}>
                    {trip?.arrive_time_name || "--:--"}
                  </div>
                </div>
              </div>

              <div className="text-end">
                <span
                  className="badge"
                  style={{
                    background: "#198754",
                    fontSize: 11,
                    padding: "6px 10px",
                  }}
                >
                  IN PROGRESS
                </span>

                <div
                  className="mt-1 d-flex align-items-center gap-1 justify-content-end"
                  style={{ fontSize: 13, color: "#0d6efd" }}
                >
                  <FileText size={13} />#{trip?.confirmation_id || "----"}
                </div>
              </div>
            </div>

            {/* ── Pickup → Drop Timeline ── */}
            <div className="mb-3">
              {/* Pickup */}
              <div className="d-flex align-items-start gap-2 mb-2">
                <MapPin size={16} color="#198754" style={{ marginTop: 2 }} />

                <div>
                  <div className="fw-semibold" style={{ fontSize: 14 }}>
                    {trip?.pickup_location || "Loading..."}
                  </div>

                  <div className="text-muted" style={{ fontSize: 11 }}>
                    Pickup Location
                  </div>
                </div>
              </div>

              {/* Line */}
              <div
                style={{
                  marginLeft: 7,
                  borderLeft: "2px dashed #dcdcdc",
                  height: 18,
                  marginBottom: 4,
                }}
              />

              {/* Drop */}
              <div className="d-flex align-items-start gap-2">
                <MapPin size={16} color="#dc3545" style={{ marginTop: 2 }} />

                <div>
                  <div className="fw-semibold" style={{ fontSize: 14 }}>
                    {trip?.dropoff_location || "Loading..."}
                  </div>

                  <div className="text-muted" style={{ fontSize: 11 }}>
                    Drop Location
                    <span
                      className="badge ms-2"
                      style={{
                        background: "#0dcaf0",
                        fontSize: 10,
                        color: "#000",
                      }}
                    >
                      GPS Live
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <hr className="my-2" />

            {/* ── Guest Section ── */}
            <div className="d-flex align-items-center gap-2">
              <div
                className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold"
                style={{
                  width: 38,
                  height: 38,
                  fontSize: 15,
                  flexShrink: 0,
                }}
              >
                {trip?.customer_name?.charAt(0)?.toUpperCase() || "G"}
              </div>

              <div style={{ lineHeight: 1.2 }}>
                <div className="fw-semibold" style={{ fontSize: 13 }}>
                  {trip?.customer_name || "Guest"}
                </div>

                <div className="text-muted" style={{ fontSize: 11 }}>
                  📞 {trip?.cust_mobileno || "No number found"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Error ── */}
        {routeError && (
          <div
            className="alert alert-danger alert-dismissible py-2 px-3 mb-3"
            style={{ fontSize: 13, borderRadius: 10 }}
          >
            ⚠️ {routeError}
            <button
              type="button"
              className="btn-close btn-sm"
              onClick={() => setRouteError("")}
            />
          </div>
        )}

        {/* ── Status bar ── */}
        <div className="d-flex align-items-center gap-2 mb-3 px-3 py-2 rounded-3 bg-success bg-opacity-10 border border-success border-opacity-25">
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "#198754",
              display: "inline-block",
              animation: "pulse 1.5s infinite",
            }}
          />
          <small className="text-success fw-semibold" style={{ fontSize: 12 }}>
            {status}
          </small>
        </div>

        {/* ── Map ── */}
        {/* ── End Trip Button ── */}
        {!hideEndTrip &&
          (trip && !tripEnded ? (
            <div className="d-grid mb-4">
              <button
                className="btn btn-danger fw-bold"
                style={{ borderRadius: 12, padding: "12px" }}
                onClick={handleEndTrip}
              >
                End Trip
              </button>
            </div>
          ) : nextTrip ? (
            <div className="alert alert-success text-center fw-semibold blinking">
              Duty for <b>{nextTrip.confirmation_id}</b> will start on{" "}
              <b>
                {new Date(nextTrip.Journey_date).toLocaleDateString("en-GB")}
              </b>{" "}
              at <b>{nextTrip.arrive_time_name}</b>
            </div>
          ) : (
            <div className="alert alert-secondary text-center">
              Loading next duty...
            </div>
          ))}
        {/* ── Map ── */}
        <div
          className="card border-0 shadow-sm mb-2"
          style={{ borderRadius: 16, overflow: "hidden" }}
        >
          {!isLoaded ? (
            <div
              className="d-flex align-items-center justify-content-center bg-white"
              style={{ height: 400 }}
            >
              <div className="text-center text-muted">
                <div className="spinner-border text-primary mb-2" />
                <div style={{ fontSize: 13 }}>Loading Map...</div>
              </div>
            </div>
          ) : (
            // ############################################################ 12.03.26
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={
                currentPosition ||
                (START_LAT
                  ? { lat: START_LAT, lng: START_LNG }
                  : { lat: 20.2961, lng: 85.8245 })
              }
              zoom={15}
              onLoad={(map) => (mapRef.current = map)}
              options={{
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: true,
                gestureHandling: "greedy",
              }}
            >
              {/* BLUE — planned route to pickup */}
              {routeToPickup.length > 1 && (
                <Polyline
                  path={routeToPickup}
                  options={{
                    strokeColor: "#0d6efd",
                    strokeOpacity: 0.55,
                    strokeWeight: 5,
                  }}
                />
              )}

              {/* GREEN — actual driven trail */}
              {drivenPath.length > 1 && (
                <Polyline
                  path={drivenPath}
                  options={{
                    strokeColor: "#198754",
                    strokeOpacity: 1,
                    strokeWeight: 4,
                  }}
                />
              )}

              {/* 🚗 Animated car (SAFEGUARDED) */}
              {currentPosition && (
                <Marker
                  position={currentPosition}
                  title="You are here"
                  icon={
                    window.google && window.google.maps
                      ? {
                          path: window.google.maps.SymbolPath
                            .FORWARD_CLOSED_ARROW,
                          scale: 6,
                          fillColor: "#1a73e8",
                          fillOpacity: 1,
                          strokeColor: "#ffffff",
                          strokeWeight: 2,
                          rotation: heading,
                          anchor: new window.google.maps.Point(0, 2.5),
                        }
                      : undefined
                  }
                />
              )}

              {/* 🧍‍♂️ 🟢 ADD THIS: LIVE CUSTOMER LOCATION BIJAY */}
              {customerPosition && (
                <Marker
                  position={customerPosition}
                  title="Customer is here"
                  icon={
                    window.google && window.google.maps
                      ? {
                          path: window.google.maps.SymbolPath.CIRCLE,
                          scale: 8,
                          fillColor: "#0d6efd",
                          fillOpacity: 1,
                          strokeColor: "#ffffff",
                          strokeWeight: 2,
                        }
                      : undefined
                  }
                />
              )}

              {/* Start point  */}
              {startPoint && (
                <Marker position={startPoint} title="Trip started here" />
              )}

              {/* Pickup point  */}
              {routeToPickup.length > 0 && (
                <Marker
                  position={routeToPickup[routeToPickup.length - 1]}
                  title={`Pickup: ${trip?.pickup_location}`}
                />
              )}
            </GoogleMap>
            // ##################################### end
          )}
        </div>
      </div>

      {/* --- OTP & ODOMETER BOTTOM SHEET --- */}
      {showOtpPopup && (
        <>
          {/* Dark transparent background overlay */}
          <div
            className="position-fixed top-0 start-0 w-100 h-100 fade-in"
            style={{ background: "rgba(0,0,0,0.6)", zIndex: 998 }}
            onClick={() => setShowOtpPopup(false)}
          ></div>

          {/* Native Bottom Sheet */}
          <div
            className="position-fixed bottom-0 start-0 w-100 bg-white slide-up-sheet"
            style={{
              zIndex: 999,
              padding: "24px 24px 40px",
              borderTopLeftRadius: "24px",
              borderTopRightRadius: "24px",
              boxShadow: "0 -4px 20px rgba(0,0,0,0.15)",
            }}
          >
            {/* Small grey pill (drag handle look) */}
            <div
              className="mx-auto mb-4"
              style={{
                width: "40px",
                height: "5px",
                backgroundColor: "#e0e0e0",
                borderRadius: "10px",
              }}
            ></div>

            <h5 className="text-center mb-1 fw-bold">End Trip Verification</h5>
            <p className="text-center text-muted small mb-4">
              Complete the final details to close this trip.
            </p>

            {/* 📸 1. ODOMETER SECTION (Photo + Reading) */}
            <div className="d-flex gap-3 mb-4">
              {/* Photo Upload Box */}
              <div className="flex-grow-0" style={{ width: "100px" }}>
                <label
                  className="d-flex flex-column align-items-center justify-content-center bg-light"
                  style={{
                    height: "100px",
                    borderRadius: "12px",
                    border: "2px dashed #ced4da",
                    cursor: "pointer",
                    overflow: "hidden",
                  }}
                >
                  {odometerPreview ? (
                    <img
                      src={odometerPreview}
                      alt="Odometer"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <>
                      {/* Inline SVG Camera Icon so you don't need extra imports */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#0d6efd"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLineJoin="round"
                        className="mb-1"
                      >
                        <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
                        <circle cx="12" cy="13" r="3" />
                      </svg>
                      <span
                        style={{
                          fontSize: "10px",
                          fontWeight: "600",
                          color: "#6c757d",
                        }}
                      >
                        Snap Odo
                      </span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setOdometerImage(file);
                        setOdometerPreview(URL.createObjectURL(file)); // Creates the preview image
                      }
                    }}
                  />
                </label>
              </div>

              {/* Odometer Reading Input */}
              <div className="flex-grow-1 d-flex flex-column justify-content-center">
                <label
                  className="fw-semibold mb-1 text-secondary"
                  style={{ fontSize: "12px", letterSpacing: "0.5px" }}
                >
                  FINAL ODOMETER
                </label>
                <div
                  className="input-group shadow-sm"
                  style={{ borderRadius: "10px" }}
                >
                  <input
                    type="number"
                    className="form-control fw-bold"
                    placeholder="e.g. 45000"
                    style={{
                      fontSize: "1.1rem",
                      borderRadius: "10px 0 0 10px",
                      backgroundColor: "#f8f9fa",
                      border: "2px solid #e9ecef",
                      borderRight: "none",
                    }}
                    onInput={(e) => {
                      if (e.target.value.length > 7)
                        e.target.value = e.target.value.slice(0, 7);
                    }}
                    value={odometerReading}
                    onChange={(e) => setOdometerReading(e.target.value)}
                  />
                  <span
                    className="input-group-text fw-bold text-muted"
                    style={{
                      borderRadius: "0 10px 10px 0",
                      backgroundColor: "#e9ecef",
                      border: "2px solid #e9ecef",
                      borderLeft: "none",
                    }}
                  >
                    KM
                  </span>
                </div>
              </div>
            </div>

            <hr className="my-4 text-muted opacity-25" />

            {/* 🔐 2. OTP SECTION */}
            <label
              className="d-block text-center fw-semibold mb-3 text-secondary"
              style={{ fontSize: "12px", letterSpacing: "0.5px" }}
            >
              CUSTOMER PIN (OTP)
            </label>
            <div className="d-flex justify-content-center gap-3 mb-4">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="1"
                  className="form-control text-center fw-bold shadow-sm"
                  style={{
                    width: "60px",
                    height: "65px",
                    fontSize: "26px",
                    borderRadius: "14px",
                    border: "2px solid #e9ecef",
                    backgroundColor: "#f8f9fa",
                  }}
                  value={digit}
                  ref={(el) => (otpRefs.current[index] = el)}
                  onChange={(e) => handleOtpChange(e.target.value, index)}
                  onKeyDown={(e) => handleOtpKeyDown(e, index)}
                  onFocus={(e) => e.target.select()}
                />
              ))}
            </div>

            {/*  SUBMIT BUTTON */}
            <button
              className="btn btn-dark w-100 fw-bold py-3 shadow"
              style={{ borderRadius: "16px", fontSize: "1.1rem" }}
              onClick={handleOtpSubmit}
            >
              Submit & Complete Trip
            </button>
          </div>
        </>
      )}

      <style>{`
  @keyframes pulse {
    0%   { opacity: 1;   transform: scale(1);   }
    50%  { opacity: 0.3; transform: scale(1.4); }
    100% { opacity: 1;   transform: scale(1);   }
  }

  .fade-in {
    animation: fadeIn 0.2s ease-out forwards;
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .slide-up-sheet {
    animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }
  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
`}</style>
    </div>
  );
};

export default SendLocationPage;